import React, { useRef, useState } from "react";
import { engine } from "./engine";
import { useEngineFrame, MiniMeter } from "./widgets";
import { Drum, Piano, Waves, AudioLines, Music2, Sparkle, FileAudio } from "lucide-react";
import { importAudioFiles, isAudioFile } from "./audioImport";

const HEAD = 230, TH = 66;
const ICON = { drums: Drum, keys: Piano, pad: Waves, bass: AudioLines, lead: Music2, pluck: Sparkle, audio: FileAudio };

function Playhead({ barW, locator }) {
  const el = useRef(null);
  useEngineFrame(() => {
    const p = engine.position();
    const s = p == null ? locator : p;
    if (el.current) el.current.style.transform = `translateX(${HEAD + (s / 16) * barW}px)`;
  });
  return <div ref={el} className="absolute top-0 bottom-0 w-px bg-white z-30 pointer-events-none" style={{ left: 0 }}><div className="absolute -top-0 -left-[5px] w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent border-t-white" /></div>;
}

function ClipView({ clip, color, selected, barW, onDown, onOpen, trackInst, barDur }) {
  const barDurRef = () => barDur;
  const w = clip.length * barW;
  const pitches = clip.notes.map((n) => n.p);
  const lo = Math.min(...pitches, 60), hi = Math.max(...pitches, lo + 6);
  const total = clip.length * 16;
  return (
    <div
      data-testid={`clip-${clip.id}`}
      onPointerDown={(e) => onDown(e, "move")}
      onDoubleClick={(e) => { e.stopPropagation(); onOpen?.(); }}
      className={`group absolute top-[3px] bottom-[3px] rounded-[3px] overflow-hidden cursor-grab active:cursor-grabbing ${selected ? "ring-2 ring-white z-20" : "z-10"} ${clip.muted ? "opacity-40" : ""}`}
      style={{ left: clip.start * barW, width: w - 1, background: color + "55", border: `1px solid ${color}` }}
    >
      <div className="h-4 px-1.5 text-[10px] font-medium truncate text-black flex items-center gap-1" style={{ background: color }}>{clip.audio && <AudioLines className="w-3 h-3" />}{clip.name}</div>
      {clip.audio ? (
        <svg className="absolute inset-x-0 top-4 bottom-0 w-full" viewBox={`0 0 ${clip.audio.peaks.length} 100`} preserveAspectRatio="none" style={{ height: "calc(100% - 16px)" }} data-testid={`waveform-${clip.id}`}>
          {clip.audio.peaks.map((p, i) => <rect key={i} x={i} y={50 - p * 0.48} width="0.8" height={Math.max(1, p * 0.96)} fill={color} opacity="0.85" />)}
        </svg>
      ) : (
      <svg className="absolute inset-x-0 top-4 bottom-0 w-full" viewBox={`0 0 ${total} 100`} preserveAspectRatio="none" style={{ height: "calc(100% - 16px)" }}>
        {clip.notes.map((n) => {
          const y = trackInst === "drums" ? ({ 36: 88, 38: 62, 39: 55, 42: 20, 46: 12, 49: 4, 45: 72, 48: 42, 37: 50 }[n.p] || 50) : 90 - ((n.p - lo) / (hi - lo || 1)) * 80;
          return <rect key={n.id} x={n.s} y={y} width={Math.max(0.6, n.l - 0.2)} height="7" fill={color} opacity={0.5 + (n.v || 0.8) / 2} />;
        })}
      </svg>
      )}
      {clip.audio && (() => { const w = clip.length * barW, secPx = w / (clip.length * barDurRef(clip)); const fi = (clip.audio.fadeIn || 0) * secPx, fo = (clip.audio.fadeOut || 0) * secPx; return (<>
        {fi > 0 && <svg className="absolute left-0 top-4 bottom-0 pointer-events-none" style={{ width: fi, height: "calc(100% - 16px)" }} viewBox="0 0 100 100" preserveAspectRatio="none"><path d="M0,100 Q40,100 100,0 L0,0 Z" fill="rgba(0,0,0,.55)" /><path d="M0,100 Q40,100 100,0" stroke="#fff" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" /></svg>}
        {fo > 0 && <svg className="absolute right-0 top-4 bottom-0 pointer-events-none" style={{ width: fo, height: "calc(100% - 16px)" }} viewBox="0 0 100 100" preserveAspectRatio="none"><path d="M0,0 Q60,100 100,100 L100,0 Z" fill="rgba(0,0,0,.55)" /><path d="M0,0 Q60,100 100,100" stroke="#fff" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" /></svg>}
        <div onPointerDown={(e) => { e.stopPropagation(); onDown(e, "fadeIn"); }} title="Drag to set fade in" data-testid={`fadein-${clip.id}`} className="absolute top-4 w-3 h-3 -mt-1 rounded-sm bg-white border border-black/60 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-amber-300 z-10" style={{ left: Math.max(0, fi - 6) }} />
        <div onPointerDown={(e) => { e.stopPropagation(); onDown(e, "fadeOut"); }} title="Drag to set fade out" data-testid={`fadeout-${clip.id}`} className="absolute top-4 w-3 h-3 -mt-1 rounded-sm bg-white border border-black/60 cursor-ew-resize opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-amber-300 z-10" style={{ right: Math.max(0, fo - 6) }} />
      </>); })()}
      <div onPointerDown={(e) => { e.stopPropagation(); onDown(e, "trimL"); }} title="Trim start" data-testid={`trim-left-${clip.id}`} className="absolute left-0 top-4 bottom-0 w-2 cursor-ew-resize hover:bg-white/30" />
      <div onPointerDown={(e) => { e.stopPropagation(); onDown(e, "resize"); }} title="Trim end" className="absolute right-0 top-4 bottom-0 w-2 cursor-ew-resize hover:bg-white/30" />
    </div>
  );
}

export default function Arrange({ song, mutate, sel, setSel, barW, locator, setLocator, snap, tool = "arrow", onSplitAt, onRename, onImportProgress, onOpenClip }) {
  const scroller = useRef(null);
  const lastDown = useRef({ id: null, t: 0, x: 0, y: 0 });
  const [editName, setEditName] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  const dropInfo = (e) => {
    const rect = scroller.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scroller.current.scrollLeft - HEAD, y = e.clientY - rect.top + scroller.current.scrollTop - 36;
    const bar = Math.max(0, Math.floor(x / barW)), ti = Math.floor(y / TH);
    return { bar, track: song.tracks[ti] };
  };
  const onDragOver = (e) => { if ([...e.dataTransfer.types].includes("Files")) { e.preventDefault(); const d = dropInfo(e); setDropAt(d); } };
  const onDrop = (e) => {
    if (![...e.dataTransfer.files].some(isAudioFile)) { setDropAt(null); return; }
    e.preventDefault(); const d = dropInfo(e); setDropAt(null);
    importAudioFiles(e.dataTransfer.files, { song, mutate, setSel, startBar: d.bar, trackId: d.track?.instrument === "audio" ? d.track.id : null, onProgress: onImportProgress });
  };
  const width = song.bars * barW;

  const rulerDown = (e, mode) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const barAt = (x) => (x - rect.left) / barW;
    if (mode === "loop") {
      const b0 = Math.floor(barAt(e.clientX));
      const move = (ev) => {
        const bx = barAt(ev.clientX);
        const lp = bx >= b0 ? [b0, Math.max(b0 + 1, Math.ceil(bx))] : [Math.max(0, Math.floor(bx)), b0 + 1];
        mutate((d) => { d.loop = [lp[0], Math.min(d.bars, lp[1])]; d.loopOn = true; });
      };
      move(e);
      const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
      window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
    } else {
      const step = Math.max(0, Math.round(barAt(e.clientX) * 16));
      setLocator(step);
      if (engine.playing) { engine.stop(); engine.play(step); }
    }
  };

  const clipDown = (e, ti, clip, mode) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    setSel({ trackId: song.tracks[ti].id, clipId: clip.id });
    if (mode === "move" && (tool === "split" || e.altKey)) {
      const el = e.currentTarget.closest("[data-testid^=clip-]") || e.currentTarget; const r = el.getBoundingClientRect();
      onSplitAt?.(song.tracks[ti].id, clip.id, clip.start + ((e.clientX - r.left) / r.width) * clip.length);
      return;
    }
    const ld = lastDown.current, nowT = performance.now();
    const isDbl = ld.id === clip.id && nowT - ld.t < 500 && Math.hypot(e.clientX - ld.x, e.clientY - ld.y) < 10;
    lastDown.current = isDbl ? { id: null, t: 0, x: 0, y: 0 } : { id: clip.id, t: nowT, x: e.clientX, y: e.clientY };
    if (mode === "move" && (isDbl || e.detail >= 2)) { onOpenClip?.(); return; }
    const x0 = e.clientX, y0 = e.clientY, s0 = clip.start, l0 = clip.length;
    const barDur = (60 / song.bpm) * 4, off0 = clip.audio?.offset || 0, fi0 = clip.audio?.fadeIn || 0, fo0 = clip.audio?.fadeOut || 0;
    let curTi = ti, dragging = false;
    const move = (ev) => {
      if (!dragging && Math.hypot(ev.clientX - x0, ev.clientY - y0) < 5) return;
      dragging = true;
      const sn = snap > 0 ? snap : 1 / 16; const db = Math.round((ev.clientX - x0) / barW / sn) * sn;
      const dt = Math.round((ev.clientY - y0) / TH);
      mutate((d) => {
        const from = d.tracks[curTi];
        const c = from.clips.find((x) => x.id === clip.id);
        if (!c) return;
        if (mode === "fadeIn" || mode === "fadeOut") {
          const dSec = ((ev.clientX - x0) / barW) * barDur, maxS = c.length * barDur;
          if (mode === "fadeIn") c.audio.fadeIn = Math.max(0, Math.min(maxS - (c.audio.fadeOut || 0), fi0 + dSec));
          else c.audio.fadeOut = Math.max(0, Math.min(maxS - (c.audio.fadeIn || 0), fo0 - dSec));
          return;
        }
        if (mode === "trimL") {
          const ns = Math.max(0, Math.min(s0 + l0 - sn, s0 + db)), delta = ns - s0;
          if (c.audio && off0 + delta * barDur < 0) return;
          c.start = ns; c.length = l0 - delta;
          if (c.audio) c.audio.offset = off0 + delta * barDur;
          else { const ds = Math.round(delta * 16); c.notes = clip.notes.filter((n) => n.s >= ds).map((n) => ({ ...n, s: n.s - ds })); }
          return;
        }
        if (mode === "move") {
          c.start = Math.max(0, Math.min(d.bars - c.length, s0 + db));
          const nt = Math.max(0, Math.min(d.tracks.length - 1, ti + dt));
          if (nt !== curTi) { from.clips = from.clips.filter((x) => x.id !== c.id); d.tracks[nt].clips.push(c); curTi = nt; }
        } else {
          const nl = Math.max(sn, l0 + db);
          if (nl !== c.length) { c.length = Math.min(nl, d.bars - c.start); c.notes = c.notes.filter((n) => n.s < c.length * 16); }
        }
      });
      if (mode === "move") setSel((s) => ({ ...s, trackId: song.tracks[curTi]?.id }));
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  const laneDbl = (e, ti) => {
    if (e.target.closest("[data-testid^=clip-]")) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const bar = Math.floor((e.clientX - rect.left) / barW);
    const id = "c_" + Math.random().toString(36).slice(2, 8);
    mutate((d) => { d.tracks[ti].clips.push({ id, name: d.tracks[ti].name, start: bar, length: 2, notes: [] }); });
    setSel({ trackId: song.tracks[ti].id, clipId: id });
  };

  return (
    <div ref={scroller} className={`relative flex-1 overflow-auto thin-scroll bg-[#1b1c20] ${dropAt ? "ring-2 ring-inset ring-sky-400" : ""} ${tool === "split" ? "cursor-crosshair" : ""}`} data-testid="arrange-view" data-tool={tool} onDragOver={onDragOver} onDragLeave={() => setDropAt(null)} onDrop={onDrop}>
      {dropAt && <div className="pointer-events-none sticky top-12 z-50 mx-auto w-max rounded-full bg-sky-400 text-black text-xs font-medium px-3 py-1 shadow-lg">Drop to import audio at bar {dropAt.bar + 1}{dropAt.track?.instrument === "audio" ? ` on ${dropAt.track.name}` : " (new track)"}</div>}
      <div className="relative" style={{ width: HEAD + width, minHeight: "100%" }}>
        {/* ruler */}
        <div className="sticky top-0 z-40 flex h-9 bg-[#2a2c31] border-b border-black">
          <div className="sticky left-0 z-10 shrink-0 bg-[#2a2c31] border-r border-black flex items-center px-3 text-[10px] font-mono text-zinc-500" style={{ width: HEAD }}>{song.tracks.length} TRACKS</div>
          <div className="relative" style={{ width }}>
            <div className="absolute inset-x-0 top-0 h-3.5 cursor-pointer" onPointerDown={(e) => rulerDown(e, "loop")} data-testid="ruler-loop">
              <div className={`absolute top-0.5 h-2.5 rounded-sm ${song.loopOn ? "bg-sky-500/70" : "bg-zinc-500/40"}`} style={{ left: song.loop[0] * barW, width: (song.loop[1] - song.loop[0]) * barW }} />
            </div>
            <div className="absolute inset-x-0 top-3.5 bottom-0 cursor-text" onPointerDown={(e) => rulerDown(e, "loc")} data-testid="ruler-time">
              {Array.from({ length: song.bars }).map((_, b) => (
                <div key={b} className="absolute top-0 bottom-0 border-l border-zinc-600" style={{ left: b * barW }}>
                  <span className="text-[10px] font-mono text-zinc-400 ml-1">{b + 1}</span>
                  {barW > 40 && [1, 2, 3].map((q) => <div key={q} className="absolute bottom-0 h-1.5 border-l border-zinc-700" style={{ left: (q * barW) / 4 }} />)}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* tracks */}
        {song.tracks.map((t, ti) => {
          const I = ICON[t.instrument] || Music2;
          const selT = sel.trackId === t.id;
          return (
            <div key={t.id} className="flex border-b border-black/70" style={{ height: TH }}>
              <div onClick={() => setSel({ trackId: t.id, clipId: sel.trackId === t.id ? sel.clipId : null })} className={`sticky left-0 z-30 shrink-0 flex border-r border-black cursor-pointer ${selT ? "bg-[#3a3d45]" : "bg-[#2c2e33] hover:bg-[#33363c]"}`} style={{ width: HEAD }} data-testid={`track-header-${ti}`}>
                <div className="w-1.5" style={{ background: t.color }} />
                <div className="flex-1 px-2 py-1.5 min-w-0 flex flex-col justify-between">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[10px] font-mono text-zinc-500 w-3">{ti + 1}</span>
                    <I className="w-3.5 h-3.5 shrink-0" style={{ color: t.color }} />
                    {editName === t.id ? (
                      <input autoFocus defaultValue={t.name} onBlur={(e) => { onRename(t.id, e.target.value); setEditName(null); }} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} className="bg-black/40 text-xs px-1 rounded w-full outline-none" />
                    ) : (
                      <span onDoubleClick={() => setEditName(t.id)} className="text-xs text-zinc-100 truncate">{t.name}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {[["M", "mute", "bg-amber-400 text-black"], ["S", "solo", "bg-yellow-300 text-black"], ["\u25CF", "arm", "bg-red-500 text-white"]].map(([lbl, k, on]) => (
                      <button key={k} data-testid={`track-${k}-${ti}`} onClick={(e) => { e.stopPropagation(); mutate((d) => { d.tracks[ti][k] = !d.tracks[ti][k]; }); }} className={`w-6 h-5 rounded-sm text-[10px] font-bold border border-black/60 ${t[k] ? on : "bg-[#1d1f23] text-zinc-400 hover:text-white"}`}>{lbl}</button>
                    ))}
                    <div className="flex-1 ml-1"><MiniMeter trackId={t.id} /></div>
                  </div>
                </div>
              </div>
              <div className="relative" style={{ width, background: `repeating-linear-gradient(90deg, transparent 0, transparent ${barW - 1}px, rgba(0,0,0,.45) ${barW - 1}px, rgba(0,0,0,.45) ${barW}px)` }} onDoubleClick={(e) => laneDbl(e, ti)} onPointerDown={() => setSel({ trackId: t.id, clipId: null })}>
                {song.loopOn && <div className="absolute inset-y-0 bg-sky-400/[.04] pointer-events-none" style={{ left: song.loop[0] * barW, width: (song.loop[1] - song.loop[0]) * barW }} />}
                {t.clips.map((c) => <ClipView key={c.id} clip={c} color={t.color} trackInst={t.instrument} selected={sel.clipId === c.id} barW={barW} barDur={(60 / song.bpm) * 4} onDown={(e, m) => clipDown(e, ti, c, m)} onOpen={() => { setSel({ trackId: t.id, clipId: c.id }); onOpenClip?.(); }} />)}
              </div>
            </div>
          );
        })}
        <div className="flex" style={{ height: 120 }}>
          <div className="sticky left-0 shrink-0 bg-[#232428] border-r border-black flex items-start justify-center pt-4 text-[11px] text-zinc-500 z-20" style={{ width: HEAD }}>Double-click a lane to add a clip</div>
        </div>
        <Playhead barW={barW} locator={locator} />
      </div>
    </div>
  );
}
